/** Format a number as a currency string (default: USD). */
export function formatCurrency(
  value: number,
  currency = 'USD',
  locale = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Format a number with thousand separators. */
export function formatNumber(value: number, decimals = 2, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Format a percentage. */
export function formatPercent(value: number, decimals = 1): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
}

/**
 * An ISO date as `01 Mar 2026`, or an em dash when there is nothing to show.
 *
 * Null-tolerant, and en-GB by default, because that is what every caller actually wanted. These two
 * functions had sat here with no callers at all: eleven screens wrote their own copy instead, since
 * the originals threw on a null date and rendered American in a system that shows dates the British
 * way everywhere else. Fixed here rather than adding a twelfth private copy.
 *
 * A string that is not a date is returned as it stands. Silently printing "Invalid Date" into an
 * audit trail is worse than showing whatever was actually stored.
 */
export function formatDate(iso: string | null | undefined, locale = 'en-GB'): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** The same, with the time: `01 Mar 2026, 14:30`. */
export function formatDateTime(iso: string | null | undefined, locale = 'en-GB'): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Abbreviate large numbers: 1 200 000 → 1.2M */
export function abbreviateNumber(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000)     return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)         return `${sign}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${abs}`;
}
